<template>
  <a
    v-if="game && !game.geoRestrict"
    :class="[$style.gameThumbnail, $style[variant]]"
    :href="gameFullPath"
    @click.prevent="gameOpen(gameCurrency)"
  >
    <div :class="[$style.gameThumbnailImgHolder, { [$style.isLoading]: !isGameLoaded }]">
      <img :src="gameImg" :class="$style.gameThumbnailImg" :alt="game.title" />
    </div>

    <BitThumbLabel
      v-if="gameLabel && variant !== 'freespins'"
      :class="$style.gameThumbnailLabel"
      :label="gameLabel"
    />

    <div :class="$style.gameThumbnailPlayIcon">
      <img
        src="/images/common/ico_play_blue.svg?url_encode"
        :class="$style.gameThumbnailPlay"
        height="56"
        alt=""
        width="56"
      />
    </div>
    <BitFavoriteIconButton
      v-if="isPlayerAuthorised && variant !== 'freespins'"
      :class="$style.gameThumbnailFavoriteBtn"
      :is-favourite="favouriteData.isFavourite"
      @click.prevent.stop="favouritesListToggle()"
    />
  </a>
  <div
    v-else-if="game && game.geoRestrict"
    :class="[$style.gameThumbnail, $style[variant], $style.restricted]"
  >
    <div :class="[$style.gameThumbnailImgHolder, { [$style.isLoading]: !isGameLoaded }]">
      <img :src="gameImg" :class="$style.gameThumbnailImg" :alt="game.title" />
    </div>
    <img
      v-if="gameProviderIcon.url"
      :class="$style.gameThumbnailProviderIcon"
      :height="gameProviderIcon.height"
      :width="gameProviderIcon.width"
      :src="gameProviderIcon.url"
      :alt="game.provider"
    />

    <BitThumbLabel
      v-if="gameLabel && variant !== 'freespins'"
      :class="$style.gameThumbnailLabel"
      :label="gameLabel"
    />

    <div :class="$style.gameRestrictedText">
      <span>{{ gameNotAvailableTitle }}</span>
    </div>
    <BitFavoriteIconButton
      v-if="isPlayerAuthorised && variant !== 'freespins'"
      :class="$style.gameThumbnailFavoriteBtn"
      :is-favourite="favouriteData.isFavourite"
      @click.prevent.stop="favouritesListToggle()"
    />
  </div>
</template>

<script lang="ts" setup>
import { BitFavoriteIconButton, BitThumbLabel } from '@bit/ui-kit';
import { useUrlConstructor } from '~/composables/useUrlConstructor';
import { useStoreGtm } from '~/stores/abstract/gtm';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreCataloguePage } from '~/stores/strapi/cataloguePage';
import { useStoreCategoryPage } from '~/stores/strapi/categoryPage';
import { useStoreComponents } from '~/stores/strapi/components';
import { useStoreProviders } from '~/stores/strapi/providers';
import { useStoreSlotTheme } from '~/stores/strapi/slotThemes';
import type { EModalName } from '~/types/modals';
import type { ISsGame } from '~/types/softswiss/ssGames';

const props = defineProps({
  game: {
    type: Object as PropType<ISsGame>,
    required: true,
  },
  gtmAction: {
    type: String,
    default: '',
  },
  /* when game is opened, modal passed by this prop will be closed  */
  closeActiveModalOnOpenGame: {
    type: [String, undefined] as PropType<EModalName | undefined>,
    required: false,
    default: undefined,
  },
  /* if this prop is passed , when user opens the game (not in demo)
        the currency will be set to this value, default is currently selected currency  */
  gameCurrency: {
    type: [String, undefined] as PropType<string | undefined>,
    required: false,
    default: undefined,
  },
  favoriteGames: {
    type: Array as PropType<number[]>,
    required: false,
  },
  imgSize: {
    type: String as PropType<'s4' | 's6'>,
    default: 's6',
  },
  isGameShouldOpenByEmit: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String as PropType<'default' | 'freespins'>,
    default: 'default',
  },
});
const emit = defineEmits<{
  (e: 'remove-from-favorite', value: number): void;
  (e: 'add-to-favorite', value: number): void;
  (e: 'openGame', value: ISsGame): void;
}>();

const storeModals = useStoreModals();
const storeGtm = useStoreGtm();
const storeSsPlayer = useStoreSsPlayer();
const storeRoot = useStoreRoot();
const providerStore = useStoreProviders();
const storeComponents = useStoreComponents();
const storeCategoryPage = useStoreCategoryPage();
const storeCataloguePage = useStoreCataloguePage();
const storeSlotTheme = useStoreSlotTheme();
const { getGameUrl } = useUrlConstructor();
const gameLabel = ref<object | null>(null);

const gameId = computed(
  () => props.game.real?.FUN?.id || props.game.real?.BTC?.id || props.game.real?.USD?.id
);

const favouriteData = computed(() => {
  if (!props.favoriteGames) {
    return {
      isFavourite: false,
      gameId: null,
    };
  }
  const favouriteGameId = props.favoriteGames.find(
    (id) =>
      id === props.game.real?.FUN?.id ||
      id === props.game.real?.BTC?.id ||
      id === props.game.real?.USD?.id
  );
  return {
    isFavourite: Boolean(favouriteGameId),
    gameId: favouriteGameId,
  };
});

const gameImg = computed(
  () => `/ss-api/sscdn/i/${props.imgSize}/${props.game.externalId || props.game.id}.webp`
);

const isGameLoaded = computed(() => props.game.externalId || props.game.id);

const gameProviderIcon = computed(() => providerStore.iconsMap[props.game.provider] || { url: '' });

const gameFullPath = computed(() =>
  getGameUrl({ id: props.game.externalId, provider: props.game.provider, title: props.game.title })
);

const hotSlotTheme = computed(() => storeSlotTheme.list.find((c) => c.categoryId === 'hot'));

const isPlayerAuthorised = computed(() => storeSsPlayer.isPlayerAuthorised);

const gameNotAvailableTitle = computed(() => storeComponents.search.gameNotAvailableTitle);

const favouritesListToggle = (): void => {
  if (props.game) {
    if (favouriteData.value.isFavourite) {
      emit('remove-from-favorite', favouriteData.value.gameId as number);
    } else {
      emit('add-to-favorite', gameId.value);

      storeGtm.push({
        vCategory: 'favorits',
        vAction: 'favorits_click',
        vLabel: props.game.title,
      });
    }
  }
};

const createGameLabel = () => {
  const isGameInHotCollection = props.game.collections.hot >= 0;
  const label = storeCategoryPage.activeLabels.find(
    (l) => props.game.collections[l.categoryId] >= 0
  );

  if (isGameInHotCollection && hotSlotTheme.value) {
    gameLabel.value = {
      title: storeCataloguePage.hotRTPLabel || '',
      customColor: hotSlotTheme.value?.backgroundColorHexEnd,
    };
  } else if (label?.categoryTitle) {
    gameLabel.value = {
      title: label.categoryTitle,
      color: label?.labelType,
    };
  }

  return null;
};

const gameOpen = (currency?: string | undefined) => {
  if (props.isGameShouldOpenByEmit) {
    emit('openGame', props.game);

    return;
  }

  if (!currency) {
    currency = storeRoot.userCurrency;
  }

  if (props.closeActiveModalOnOpenGame) {
    storeModals.closeModal(props.closeActiveModalOnOpenGame);
  }

  if (
    storeSsPlayer.isPlayerAuthorised &&
    (usePlatform().isMobile || usePlatform().isTablet) &&
    props.game &&
    props.game.real[currency] &&
    props.game.real[currency].id
  ) {
    storeGtm.push({
      vCategory: currency === 'FUN' ? 'demo' : 'playnow',
      vAction: props.gtmAction,
      vLabel: props.game.externalId,
    });
  }

  return navigateTo({ path: gameFullPath.value });
};

// watch(
//   () => gameImg,
//   ({ value }) => {
//     if (value) {
//       useHead({
//         link: [
//           {
//             rel: 'preload',
//             href: value,
//             as: 'image',
//             crossorigin: '',
//           },
//         ],
//       });
//     }
//   },
//   { immediate: true }
// );

onMounted(() => {
  createGameLabel();
});
</script>

<style lang="scss" module src="./gameThumbnail.scss"></style>
